import React, { FC, useCallback, useMemo } from 'react'
import { components } from 'react-select'
import type { Props as SelectProps } from 'react-select'
import { useField, useFormikContext } from 'formik'
import cx from 'classnames'

import { FormLabelProps } from '../../FormLabel'

import * as SC from './styled'

export type SelectOption = {
  label: string
  value: string
  form?: string
}

export type GroupedSelectOption = {
  label: string
  options: Array<SelectOption>
}

export type FormFieldDropdownSelectProps = Omit<
  SelectProps<SelectOption, false | true, GroupedSelectOption>,
  'onChange' | 'value'
> & {
  className?: string
  name: string
  error?: string
  errors?: { message?: string }[]
  helpText?: string
  label?: string
  labelProps?: FormLabelProps
  disabled?: boolean
  leftAdornment?: React.ReactElement
  onInputChangeCustom?: (evt: SelectOption[]) => void
}

const Option = (props: any) => {
  return (
    <div>
      <components.Option {...props}>
        <SC.Checkbox
          type={'checkbox'}
          name={props?.name}
          checked={props.isSelected}
          label={props.label}
          onChange={() => null}
        />
      </components.Option>
    </div>
  )
}

const ValueContainer = (props: any) => {
  return (
    <components.ValueContainer {...props}>
      {props.children}
    </components.ValueContainer>
  )
}

const Control = (props: any) => {
  return (
    <components.Control {...props}>
      <SC.LeftSlotPlaceholderContainer>
        {props.selectProps.leftAdornment && (
          <SC.LeftSlot>{props.selectProps.leftAdornment}</SC.LeftSlot>
        )}
        <components.Placeholder {...props} isFocused={props.isFocused}>
          {props.selectProps.placeholder}
        </components.Placeholder>
      </SC.LeftSlotPlaceholderContainer>
      {props.children}
    </components.Control>
  )
}

const FormFieldDropdownSelect: FC<FormFieldDropdownSelectProps> = ({
  className,
  name,
  error,
  helpText,
  placeholder,
  label,
  labelProps,
  options,
  disabled,
  onInputChangeCustom,
  ...inputProps
}) => {
  const [field] = useField(name)
  const { setFieldValue } = useFormikContext()

  const flattenedOptions = useMemo(
    () =>
      options?.flatMap((o: SelectOption | GroupedSelectOption) => {
        const isNotGrouped = 'value' in o
        if (isNotGrouped) {
          return o
        } else {
          return o.options
        }
      }),
    [options]
  )

  const value = useMemo(
    () =>
      flattenedOptions?.filter((o) => {
        const isArrayValue = Array.isArray(field.value)

        if (isArrayValue) {
          const values = field.value as Array<any>
          return values.includes(o.value)
        } else {
          return field.value === o.value
        }
      }),
    [flattenedOptions, field]
  )

  const handleChange = useCallback(
    (val: any) => {
      const _val = val as SelectOption[] | SelectOption
      const isArray = Array.isArray(_val)
      if (isArray) {
        const values = _val.map((o) => o.value)
        setFieldValue(name, values)
      } else {
        setFieldValue(name, _val.value)
      }
    },
    [setFieldValue, name]
  )

  const renderInput = () => {
    return (
      /* @ts-ignore */
      <SC.Input
        {...inputProps}
        classNamePrefix={'react-select'}
        aria-label={label}
        value={value}
        name={name}
        placeholder={placeholder}
        $isActive={!!value}
        $isError={!!error}
        $isDisabled={disabled}
        $isSearchable={inputProps.isSearchable}
        options={options}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
        hideSelectedOptions={false}
        menuPosition={'fixed'}
        menuPlacement={'auto'}
        components={{
          Option,
          ValueContainer,
          Control,
        }}
        onChange={(newValue) => {
          const selectedValues = Array.isArray(newValue)
            ? (newValue as SelectOption[])
            : [newValue as SelectOption]

          handleChange(selectedValues)
          if (onInputChangeCustom) {
            onInputChangeCustom(selectedValues)
          }
        }}
        minMenuHeight={40}
      />
    )
  }

  return (
    <SC.Container className={cx('FormFieldDropdownSelect', className)}>
      {label && <SC.Label {...labelProps}>{label}</SC.Label>}
      {renderInput()}
      {!error && helpText && <SC.HelpMessage>{helpText}</SC.HelpMessage>}
      {error && <SC.ErrorMessage>{error}</SC.ErrorMessage>}
    </SC.Container>
  )
}

export default FormFieldDropdownSelect
